import { useState, useMemo } from "react";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { Table } from "reactstrap";
import { FaArrowUp, FaArrowDown } from "react-icons/fa6";

export default function DataGrid({
  rows,
  columns,
  noDataMessage,
  onRowClick,
  rowClassName,
  rowStyle,
  initialSorting,
  getRowId,
}) {
  const [sorting, setSorting] = useState(initialSorting || []);

  const tableColumns = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id ?? col.key,
        accessorKey: col.key,
        header: col.header,
        size: col.width ?? 150,
        cell: col.cell ?? ((props) => props.getValue()),
        enableSorting: col.sortable !== false,
        enableResizing: true,
        sortingFn:
          col.sortable === false
            ? undefined
            : (col.sortingFn ?? "alphanumeric"),
      })),
    [columns],
  );

  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: "onChange",
    ...(getRowId && { getRowId }),
  });

  return (
    <div className="card">
      <Table className="table-striped" style={{ width: "100%" }}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{
                    position: "relative",
                    width: header.getSize(),
                    userSelect: "none",
                    color: header.column.getIsSorted() ? "#20a8d8" : undefined,
                  }}
                >
                  <div
                    className="d-flex align-items-center"
                    style={{
                      cursor: header.column.getCanSort()
                        ? "pointer"
                        : "default",
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getCanSort() && (
                      <span className="ms-1">
                        {
                          {
                            asc: <FaArrowUp />,
                            desc: <FaArrowDown />,
                          }[header.column.getIsSorted()]
                        }
                      </span>
                    )}
                  </div>
                  {header.column.getCanResize() && (
                    <span
                      className={`resizer ${
                        header.column.getIsResizing() ? "isResizing" : ""
                      }`}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                    >
                      <div className="divider-line" />
                      <div className="divider-line" />
                    </span>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 && noDataMessage ? (
            <tr>
              <td
                colSpan={columns.length}
                className="text-center py-4 text-muted"
              >
                {noDataMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={typeof rowClassName === "function" ? rowClassName(row) : (rowClassName || "")}
                style={rowStyle}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="align-content-center">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
}
